// ts-rest contract for the agent runner endpoint, plus the typed surfaces
// that supersede the legacy `/api/staff/actions/:name` registry
// (OLD_CODE.html #2): composer triggers and the reviewable approve / reject
// / request-revision family.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---- Reviewable family (OLD_CODE.html #2 replacement) ----
//
// The reviewable kind names the artefact the staff reviewer is acting on.
// Kebab-case, deliberately not the underlying table name — the wire surface
// must not leak storage shape. Mapping kind -> table + audit kind +
// materialise hook + allowed personas lives in the server-side
// `ReviewableRegistry`.
//
// Two large groups currently:
//  - Detector findings (the SENTINEL / PULSE / etc. -finding tables).
//  - Composer drafts (the SCRIVENER / HERALD / etc. -draft tables, minus
//    suitability-letter-draft which has bespoke materialise side-effects
//    and stays under `comply-suitability` contract).
//  - ATLAS desk artefacts (the call-* triple + monthly-narrative-draft).
export const reviewableKindSchema = z.enum([
    // Detector findings
    'vulnerability-marker-finding',
    'onboarding-stall-finding',
    'investment-trust-policy-finding',
    'data-freshness-finding',
    'pcf-deviation-finding',
    'reconciliation-classification-finding',
    'kyc-freshness-finding',
    'sanctions-hit-finding',
    'cd-rescore-finding',
    // CEDAR — CA drafts extracted from an uploaded PDF (ADR-0016)
    'coac-action-draft',
    // CEDAR — on-demand plain-English CA breakdown (informational artefact)
    'coac-breakdown',
    // Composer drafts
    'onboarding-pack-draft',
    'client-communication-draft',
    'fund-report-draft',
    'vulnerability-adaptation-plan-draft',
    'governance-paper-draft',
    'fair-value-assessment-draft',
    // ATLAS desk artefacts
    'call-extracted-fact',
    'call-action-item',
    'call-kb-note',
    'monthly-narrative-draft',
]);
export type ReviewableKind = z.infer<typeof reviewableKindSchema>;

// Composer agents that the `triggerComposer` endpoint dispatches to.
export const composerAgentSchema = z.enum([
    'scrivener',
    'herald',
    'emissary',
    'charter',
    'haven',
    'forum',
    'arbiter',
]);
export type ComposerAgent = z.infer<typeof composerAgentSchema>;

// Shared response envelope for the typed endpoints that supersede the
// `/api/staff/actions/:name` surface. Preserves `{ ok, data, invalidate }`
// so the existing React-Query cache-bust logic in the web doesn't change.
export const staffActionOkSchema = z.object({
    ok: z.literal(true),
    data: z.record(z.string(), z.unknown()).optional(),
    invalidate: z.array(z.string()).optional(),
});
export const staffActionErrorSchema = z.object({
    ok: z.literal(false),
    error: z.string(),
});
export type StaffActionOk = z.infer<typeof staffActionOkSchema>;
export type StaffActionError = z.infer<typeof staffActionErrorSchema>;

// The reviewable / approve / reject / request-revision / composer endpoints all
// answer with the same staff-action response shape: a `staffActionOkSchema` 200
// and `staffActionErrorSchema` for 400/403/(404). Shared response maps so the
// quad isn't copy-pasted per endpoint. ts-rest reads `responses` structurally,
// so referencing a shared const is behaviour-preserving.
export const staffActionResponses = {
    200: staffActionOkSchema,
    400: staffActionErrorSchema,
    403: staffActionErrorSchema,
    404: staffActionErrorSchema,
} as const;
export const staffActionResponsesNoNotFound = {
    200: staffActionOkSchema,
    400: staffActionErrorSchema,
    403: staffActionErrorSchema,
} as const;

/**
 * Discriminated union the web's `runStaffEndpoint` returns: the same shape
 * the legacy `/api/staff/actions/:name` surface used (`{ok, data?, error?,
 * invalidate?}`), kept as a single type so callers can keep their existing
 * `if (result.ok) ...` glue.
 */
export type StaffActionResult =
    | { ok: true; data?: Record<string, unknown>; invalidate?: string[] }
    | { ok: false; error: string };

// ---- Batch review decisions (the single review write surface) ----
//
// One endpoint commits any number of review decisions in a single request.
// A singular accept/reject is just an array of one; the call-drafts panel
// stages several and confirms them together. Two decision shapes:
//   - `artefact` — act on a reviewable artefact by its kebab `kind` + id
//     (the same `ReviewAgentArtefact` Action the old per-kind routes used).
//   - `task` — act on an AgentTask: the server resolves the job's primary
//     artefact and acts on its Reviewable status, or audits at job level when
//     the job produced no artefact.
export const reviewDecisionVerbSchema = z.enum(['approve', 'reject', 'request_revision']);
export type ReviewDecisionVerb = z.infer<typeof reviewDecisionVerbSchema>;

export const artefactReviewDecisionSchema = z.object({
    target: z.literal('artefact'),
    kind: reviewableKindSchema,
    id: z.string(),
    decision: reviewDecisionVerbSchema,
    // Reviewer note — required by the server for reject / request_revision
    // (it defaults an empty reject note to a placeholder; request_revision
    // demands a real one).
    reason: z.string().optional(),
    // Per-kind approve extras (e.g. monthly-narrative picked variant), routed
    // through the kind's `extraPatch`.
    extra: z.record(z.string(), z.unknown()).optional(),
});

export const taskReviewDecisionSchema = z.object({
    target: z.literal('task'),
    taskId: z.string(),
    // Tasks only approve/reject (request-revision is artefact-kind specific).
    decision: z.enum(['approve', 'reject']),
    reason: z.string().optional(),
});

export const reviewDecisionSchema = z.discriminatedUnion('target', [
    artefactReviewDecisionSchema,
    taskReviewDecisionSchema,
]);
export type ReviewDecision = z.infer<typeof reviewDecisionSchema>;

// Per-decision outcome. `ref` echoes the artefact id / taskId so the caller can
// correlate a failure back to the row it staged.
export const reviewDecisionResultSchema = z.object({
    ref: z.string(),
    ok: z.boolean(),
    error: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
});
export type ReviewDecisionResult = z.infer<typeof reviewDecisionResultSchema>;

export const reviewDecisionsRequestSchema = z.object({
    decisions: z.array(reviewDecisionSchema).min(1),
});

// The batch always answers 200 when the actor is admitted; per-decision
// failures live in `results` (partial success is honest, never aborts the
// batch). `summary` + `invalidate` (union of every decision's cache-bust keys)
// drive the one summary toast + React-Query invalidation in the web.
export const reviewDecisionsResponseSchema = z.object({
    ok: z.literal(true),
    results: z.array(reviewDecisionResultSchema),
    summary: z.object({ confirmed: z.number(), failed: z.number() }),
    invalidate: z.array(z.string()).optional(),
});
export type ReviewDecisionsResponse = z.infer<typeof reviewDecisionsResponseSchema>;

// The runner accepts either of two request shapes:
//   - `{ taskId }` — run an existing AgentJob by id.
//   - `{ type }`   — queue a fresh AgentJob for that task type, then run
//     it. The `type`-based form is used by the in-app trigger path
//     (AgentContext.startTask) and per-PRD Playwright fixtures so a
//     caller can fire an agent without first inserting a task.
export const agentRunRequestSchema = z.object({
    taskId: z.string().optional(),
    type: z.string().optional(),
    trigger: z.string().optional(),
    clientId: z.string().optional(),
    clientName: z.string().optional(),
    frontend: z.enum(['desk', 'ops', 'comply', 'view', 'global']).optional(),
    workflow: z.string().optional(),
    input: z.record(z.string(), z.unknown()).optional(),
    payload: z.record(z.string(), z.unknown()).optional(),
});

export const agentRunResponseSchema = z.object({
    ok: z.boolean(),
    taskId: z.string(),
    state: z.string().optional(),
    output: z.unknown().optional(),
    error: z.string().optional(),
});

// "Run X now". The runner picks the detector's source row (an open
// ontology candidate or an enriching working-entity row, depending on the
// detector mode), fires the real agent, and emits a real Finding.
// Returns the queued AgentTask id; the runner advances it.
//
// Originally `/api/demo/run-detector/:agent` (PRD #67); reframed as an
// ops affordance at `/api/ops/detectors/:agent/run` (ADR-0006, issue
// #92) — useful in production for re-running a detector after a config
// change or after backfilling source data. Persona-gated and audited
// at the controller.
export const runDetectorResponseSchema = z.object({
    ok: z.boolean(),
    agentTaskId: z.string().optional(),
    candidateId: z.string().optional(),
    error: z.string().optional(),
});

// Typed `POST /api/agent/tasks` — queues an AgentJob and returns the
// composed task row. Used by AgentContext, ComposerTriggerButton,
// MonthlyNarrativePanel, and desk/calls-meetings.
export const agentTaskInsertSchema = z
    .object({
        id: z.string().optional(),
    })
    .catchall(z.unknown());

export const agentTaskRowSchema = z.object({ id: z.string() }).catchall(z.unknown());

// Typed `PATCH /api/agent/tasks/:id` for the same callers.
export const agentTaskPatchSchema = z.record(z.string(), z.unknown());

// Query for `GET /api/agent/tasks`. All optional; query values arrive as
// strings, so `limit`/`offset` are coerced. `status` / `type` accept a
// comma-separated list for an IN-filter (e.g. `?status=queued,running`); the
// handler splits on `,`. The web's existing pollers pass none of these, so
// the default (limit 200) is unaffected.
export const agentTaskListQuerySchema = z
    .object({
        status: z.string().optional(),
        type: z.string().optional(),
        surface: z.string().optional(),
        clientId: z.string().optional(),
        limit: z.coerce.number().int().positive().max(1000).optional(),
        offset: z.coerce.number().int().nonnegative().optional(),
    })
    .optional();
export type AgentTaskListQuery = z.infer<typeof agentTaskListQuerySchema>;

// Query for `GET /api/agent/worklist` — the caller's owner-scoped Agent Kanban
// (ADR-0022). Deliberately carries NO scope / owner / surface / clientId input:
// the worklist is identity-derived server-side from `ctx.actor`, never a
// parameter. Only pagination is accepted.
export const agentWorklistQuerySchema = z
    .object({
        limit: z.coerce.number().int().positive().max(1000).optional(),
        offset: z.coerce.number().int().nonnegative().optional(),
    })
    .optional();
export type AgentWorklistQuery = z.infer<typeof agentWorklistQuerySchema>;

// -----------------------------------------------------------------------
// Scheduled runs (WS-E) — the Kanban "Scheduled" tab's read surface.
//
// One flat row per scheduled unit of work, unioning named-agent schedule
// DECLARATIONS and enabled workflow schedules. `active` is honest about
// declared-vs-firing: an agent whose cron is actually registered is `true`,
// a declared-but-not-firing agent is `false`; a workflow is `active` when
// its `workflow:<id>` cron is live in the SchedulerRegistry.
// -----------------------------------------------------------------------
export const scheduledRunSchema = z.object({
    name: z.string(),
    kind: z.enum(['agent', 'workflow']),
    cron: z.string(),
    // Computed from the cron at read time; null when the cron is invalid.
    nextRunAt: z.string().nullable(),
    // Workflow: WorkflowSchedule.lastRunAt. Agent: best-effort newest scheduled AgentJob.
    lastRunAt: z.string().nullable(),
    surface: z.enum(['desk', 'ops', 'comply', 'view']),
    // Whether a cron is actually registered/firing for this row.
    active: z.boolean(),
    // workflowId (deep-link) for workflow rows; agentName for agent rows.
    ref: z.string().nullable(),
});
export type ScheduledRun = z.infer<typeof scheduledRunSchema>;

export const listScheduledRunsResponseSchema = z.object({
    scheduled: z.array(scheduledRunSchema),
});
export type ListScheduledRunsResponse = z.infer<typeof listScheduledRunsResponseSchema>;

export const agentContract = c.router(
    {
        run: {
            method: 'POST',
            path: '/agent/run',
            body: agentRunRequestSchema,
            responses: {
                200: agentRunResponseSchema,
                400: z.object({ error: z.string() }),
            },
            summary: 'Run an agent workflow',
        },
        insertTask: {
            method: 'POST',
            path: '/agent/tasks',
            body: agentTaskInsertSchema,
            responses: {
                200: agentTaskRowSchema,
                400: z.object({ error: z.string() }),
            },
            summary: 'Queue an AgentJob and return the composed task row (used by the web UI)',
        },
        patchTask: {
            method: 'PATCH',
            path: '/agent/tasks/:id',
            pathParams: z.object({ id: z.string() }),
            body: agentTaskPatchSchema,
            responses: {
                200: agentTaskRowSchema,
                400: z.object({ error: z.string() }),
                404: z.object({ error: z.string() }),
            },
            summary: 'Transition an AgentJob (patch task status)',
        },
        listTasks: {
            method: 'GET',
            path: '/agent/tasks',
            // Server-side filters honoured by the handler. `status` / `type`
            // accept a single value or a comma-separated list (IN-filter);
            // `surface` / `clientId` push down to the resolver. `limit` /
            // `offset` paginate the composed result (default limit 200, so
            // existing web callers that pass nothing are unaffected).
            query: agentTaskListQuerySchema,
            responses: {
                200: z.object({ tasks: z.array(agentTaskRowSchema) }),
                400: z.object({ error: z.string() }),
            },
            summary:
                'List composed AgentTask rows the web UI polls (filtered + paginated server-side)',
        },
        listWorklist: {
            method: 'GET',
            path: '/agent/worklist',
            // The caller's owner-scoped Agent Kanban (ADR-0022). Self-scoped:
            // the server resolves the worklist from `ctx.actor` and accepts no
            // scope input, so this route can never return another user's (or
            // the firm-wide) list. The Agent Activity Log uses `listTasks`.
            query: agentWorklistQuerySchema,
            responses: {
                200: z.object({ tasks: z.array(agentTaskRowSchema) }),
                400: z.object({ error: z.string() }),
            },
            summary: "List the caller's owner-scoped agent worklist (identity-derived; ADR-0022)",
        },
        listScheduled: {
            method: 'GET',
            path: '/agent/scheduled',
            responses: {
                200: listScheduledRunsResponseSchema,
            },
            summary:
                'List scheduled runs (agent schedule declarations + enabled workflow schedules) for the Kanban "Scheduled" tab',
        },
        listFindings: {
            method: 'GET',
            path: '/agent/findings/:agent',
            pathParams: z.object({ agent: z.string() }),
            query: z.record(z.string(), z.string()).optional(),
            responses: {
                200: z.object({
                    findings: z.array(z.object({ id: z.string() }).catchall(z.unknown())),
                }),
                400: z.object({ error: z.string() }),
            },
            summary: 'Per-detector finding rows the generic `<DetectorFindingFeed>` consumes',
        },
        listAgentSessions: {
            method: 'GET',
            path: '/agent/sessions',
            responses: {
                200: z.object({
                    sessions: z.array(z.object({ id: z.string() }).catchall(z.unknown())),
                }),
                400: z.object({ error: z.string() }),
            },
            summary: 'List `agent_sessions` rows the AgentSessionsPanel polls',
        },
        listAgentTraces: {
            method: 'GET',
            path: '/agent/traces',
            query: z.object({ artefactId: z.string().optional() }).optional(),
            responses: {
                200: z.object({
                    traces: z.array(z.object({ id: z.string() }).catchall(z.unknown())),
                }),
                400: z.object({ error: z.string() }),
            },
            summary: 'List `agent_traces` rows (glass-box trace)',
        },
        listAgentRows: {
            method: 'GET',
            path: '/agent/rows/:table',
            pathParams: z.object({ table: z.string() }),
            query: z.record(z.string(), z.string()).optional(),
            responses: {
                200: z.object({
                    rows: z.array(z.object({ id: z.string() }).catchall(z.unknown())),
                }),
                400: z.object({ error: z.string() }),
            },
            summary:
                'Allow-listed read of agent-artefact tables used by ReviewQueueView, MonthlyNarrativePanel, and the composition-workbench',
        },

        // -----------------------------------------------------------------
        // Batch review decisions (the single review write surface) — typed,
        // persona-gated, actor-attributed. Supersedes the per-kind
        // approve/reject/request-revision routes AND the agent-task
        // approve/reject routes: every review decision (artefact or task,
        // singular or staged-batch) commits here in one request via the same
        // `ReviewAgentArtefact` Action.
        // -----------------------------------------------------------------
        reviewDecisions: {
            method: 'POST',
            path: '/agent/reviewables/decisions',
            body: reviewDecisionsRequestSchema,
            responses: {
                200: reviewDecisionsResponseSchema,
                400: staffActionErrorSchema,
                403: staffActionErrorSchema,
            },
            summary:
                'Apply a batch of review decisions (approve/reject/request-revision) over artefacts and/or tasks in one request',
        },

        // -----------------------------------------------------------------
        // Composer trigger (OLD_CODE.html #2 replacement for the seven
        // `trigger-<agent>` named actions).
        // -----------------------------------------------------------------
        triggerComposer: {
            method: 'POST',
            path: '/agent/composers/:agent/trigger',
            pathParams: z.object({ agent: composerAgentSchema }),
            // Per-agent body validation (mirrors `requireStringField` plumbing
            // in agent-triggers.ts) lives in the controller; the wire shape is
            // free-form so we don't have to mint seven separate endpoint
            // signatures.
            body: z.record(z.string(), z.unknown()),
            responses: staffActionResponsesNoNotFound,
            summary: 'Trigger a composer agent (queues an AgentTask of the matching type)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
